import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

/**
 * Reusable review-and-confirm dialog.
 * Props:
 *   trigger        – ReactNode that opens the dialog (usually the action button)
 *   title, subtitle
 *   details        – object of { label: value } rows to show, or a ReactNode for custom layout
 *   confirmLabel   – label of the confirm button
 *   confirmTone    – 'primary' | 'success' | 'danger' | 'warning'
 *   requireComment – bool, shows a Textarea and passes text to onConfirm
 *   commentLabel   – label above the textarea
 *   commentRequired– validate non-empty comment before confirm
 *   onConfirm      – async (comment?) => void
 *   testIdConfirm  – data-testid for the confirm button
 */
export function ReviewConfirmDialog({
  trigger, title, subtitle, details, confirmLabel = "Confirm",
  confirmTone = "primary", requireComment = false, commentLabel = "Comment",
  commentRequired = false, onConfirm, testIdConfirm, testIdOpen,
}) {
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const toneClass = {
    primary: "bg-[#0052FF] hover:bg-[#0043D9] text-white",
    success: "bg-emerald-600 hover:bg-emerald-700 text-white",
    danger: "bg-rose-600 hover:bg-rose-700 text-white",
    warning: "bg-amber-500 hover:bg-amber-600 text-white",
  }[confirmTone];

  const handleConfirm = async () => {
    if (commentRequired && !comment.trim()) return;
    setBusy(true);
    try {
      await onConfirm(comment.trim());
      setOpen(false);
      setComment("");
    } finally {
      setBusy(false);
    }
  };

  const triggerWithTestId = testIdOpen && React.isValidElement(trigger)
    ? React.cloneElement(trigger, {
        "data-testid": testIdOpen,
        onClick: (e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); },
      })
    : React.cloneElement(trigger, {
        onClick: (e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); },
      });

  const renderDetails = () => {
    if (React.isValidElement(details)) return details;
    if (details && typeof details === "object") {
      return (
        <dl className="text-sm divide-y">
          {Object.entries(details).map(([k, v]) => (
            <div key={k} className="py-2 grid grid-cols-3 gap-2">
              <dt className="col-span-1 text-xs font-semibold text-slate-500 uppercase tracking-widest self-center">{k}</dt>
              <dd className="col-span-2 text-slate-800 break-words whitespace-pre-wrap">{v ?? "—"}</dd>
            </div>
          ))}
        </dl>
      );
    }
    return null;
  };

  return (
    <>
      {triggerWithTestId}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="review-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {subtitle && <DialogDescription>{subtitle}</DialogDescription>}
          </DialogHeader>
          <div className="bg-slate-50 border rounded-lg p-3 mt-1">
            {renderDetails()}
          </div>
          {requireComment && (
            <div className="mt-3">
              <label className="text-xs font-semibold text-slate-600 mb-1 block">{commentLabel}{commentRequired ? " *" : ""}</label>
              <Textarea data-testid="review-confirm-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="Add a note the other party will see…" />
            </div>
          )}
          <DialogFooter className="mt-4 gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid={testIdConfirm} onClick={handleConfirm} disabled={busy || (commentRequired && !comment.trim())} className={toneClass}>
              {busy ? "Please wait…" : confirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ReviewConfirmDialog;
